import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import {
  EmployeeDetail,
  type EmployeeTab,
  type EmployeeTabGroup,
} from '@/features/hr-employees/employee-detail'
import { employeeTabKeys } from '@/features/hr-employees/employee-detail-tabs'
import { VIOLATION_STATUSES } from '@/features/hr-violations/violation-status'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for one employee's record.
 *
 * The guard checks `employee.view` only. Whether the personal and financial
 * SECTIONS render is decided by what the API returned, not by a second grant
 * check here — the payload omits a group the caller may not see, and the screen
 * renders what arrived. Checking grants in both places would be two
 * implementations of one rule.
 *
 * ## The `tab` search param
 *
 * The tab strip is linkable view state (`apps/company/AGENTS.md` — filters and
 * view state live in the URL): the HR landing's expiry watch deep-links to
 * `/hr/employees/{id}?tab=documents`. Validated here at the boundary; the
 * screen falls back to overview for a tab its caller may not open.
 *
 * ## Why this renders an Outlet
 *
 * `employees.$employeeId.edit.tsx` is a CHILD of this route in flat file
 * routing — the generated tree gives it
 * `getParentRoute: () => AuthenticatedHrEmployeesEmployeeIdRoute`. Without an
 * Outlet the child never mounts, so `/hr/employees/{id}/edit` silently rendered
 * this screen instead and the Edit button went nowhere. Slice 8 found it in the
 * browser.
 *
 * Rendering the detail *or* the child, rather than both, is what keeps this a
 * shell rather than a layout: there is no shared chrome between a record and
 * its edit form. Renaming this file to `…$employeeId.index.tsx` would let
 * TanStack do the same thing structurally and is the tidier fix — it needs a
 * file rename, which `AGENTS.md` §2 says to ask about first.
 */
type EmployeeSearch = {
  tab?: EmployeeTab
  focus?: 'basic'
  history?: boolean
  return_run?: string
  status?: (typeof VIOLATION_STATUSES)[number]
}

export const Route = createFileRoute('/_authenticated/hr/employees/$employeeId')({
  validateSearch: (search: Record<string, unknown>): EmployeeSearch => ({
    tab: employeeTabKeys.find((candidate) => candidate === search.tab),
    focus: search.focus === 'basic' ? 'basic' : undefined,
    history: search.history === true || search.history === '1',
    return_run: typeof search.return_run === 'string' ? search.return_run : undefined,
    status: VIOLATION_STATUSES.find((status) => status === search.status),
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'employee.view')) {
      throw redirect({ to: '/' })
    }
  },
  component: EmployeeDetailRoute,
})

function EmployeeDetailRoute() {
  const navigate = useNavigate()
  const { employeeId } = Route.useParams()
  const search = Route.useSearch()
  const children = useChildMatches()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <EmployeeDetail
      employeeId={employeeId}
      tab={search.tab}
      salarySearch={
        search.tab === 'salary-components'
          ? { focus: search.focus, history: search.history, return_run: search.return_run }
          : undefined
      }
      violationStatus={search.status}
      onTabChange={(tab: EmployeeTabGroup) => {
        // `replace: true` — stepping back through every tab the user clicked is
        // not a history anyone wants (the attendance shell does the same).
        void navigate({
          to: '/hr/employees/$employeeId',
          params: { employeeId },
          search: (current) => ({ ...current, tab }),
          replace: true,
        })
      }}
      onViolationStatusChange={(status) => {
        void navigate({
          to: '/hr/employees/$employeeId',
          params: { employeeId },
          search: (current) => ({
            ...current,
            status: VIOLATION_STATUSES.find((candidate) => candidate === status),
          }),
          replace: true,
        })
      }}
    />
  )
}
